export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
}

type AuthChangeCallback = (user: User | null) => void

/**
 * AuthStore manages user authentication state.
 * Each instance is scoped to a specific API key prefix to prevent conflicts
 * when multiple widgets from different tenants are on the same page.
 */
class AuthStore {
  private listeners: Set<AuthChangeCallback> = new Set()
  private user: User | null = null
  private keyPrefix: string = 'yoga_widget'
  /**
   * Gæste-køb (walk-in uden konto): tokens holdes KUN i hukommelsen, aldrig i
   * localStorage. Sessionen findes kun for at drive booking + betaling. Den dør,
   * når siden forlades (betalings-redirect) eller booking-vinduet lukkes
   * (endGuestSession), så en walk-in aldrig efterlades logget ind på studiets
   * hjemmeside. Samme regel som den native flade, der logger gæsten ud igen.
   */
  private guestTokens: { access: string; refresh: string } | null = null

  /**
   * Initialize with an API key to scope storage keys per tenant.
   * Uses the key_prefix (first 20 chars) to namespace localStorage.
   */
  init(apiKey: string) {
    // Extract prefix from API key (e.g., "wk_live_abc123..." -> "wk_live_abc123")
    this.keyPrefix = `yoga_${apiKey.substring(0, 20)}`
    this.loadFromStorage()
  }

  private get storageKeyAccess(): string {
    return `${this.keyPrefix}_access_token`
  }

  private get storageKeyRefresh(): string {
    return `${this.keyPrefix}_refresh_token`
  }

  private get storageKeyUser(): string {
    return `${this.keyPrefix}_user`
  }

  private loadFromStorage() {
    try {
      const userJson = localStorage.getItem(this.storageKeyUser)
      if (userJson) {
        this.user = JSON.parse(userJson)
        this.notifyListeners()
      }
    } catch {
      // Ignore errors
    }
  }

  getAccessToken(): string | null {
    if (this.guestTokens) return this.guestTokens.access
    try {
      return localStorage.getItem(this.storageKeyAccess)
    } catch {
      return null
    }
  }

  getRefreshToken(): string | null {
    if (this.guestTokens) return this.guestTokens.refresh
    try {
      return localStorage.getItem(this.storageKeyRefresh)
    } catch {
      return null
    }
  }

  getUser(): User | null {
    return this.user
  }

  isLoggedIn(): boolean {
    return this.user !== null && this.getRefreshToken() !== null
  }

  setTokens(accessToken: string, refreshToken: string) {
    // Token-fornyelse under et gæste-køb bliver i hukommelsen.
    if (this.guestTokens) {
      this.guestTokens = { access: accessToken, refresh: refreshToken }
      return
    }
    try {
      localStorage.setItem(this.storageKeyAccess, accessToken)
      localStorage.setItem(this.storageKeyRefresh, refreshToken)
    } catch {
      // Ignore storage errors
    }
  }

  setUser(user: User) {
    this.user = user
    if (this.guestTokens) {
      this.notifyListeners()
      return
    }
    try {
      localStorage.setItem(this.storageKeyUser, JSON.stringify(user))
    } catch {
      // Ignore storage errors
    }
    this.notifyListeners()
  }

  /** Start et gæste-køb: tokens og bruger kun i hukommelsen (se guestTokens). */
  setGuestSession(accessToken: string, refreshToken: string, user: User) {
    this.guestTokens = { access: accessToken, refresh: refreshToken }
    this.user = user
    this.notifyListeners()
  }

  isGuestSession(): boolean {
    return this.guestTokens !== null
  }

  /**
   * Afslut et gæste-køb (booking-vinduet lukkes). Rører ALDRIG et rigtigt
   * medlems login: uden en aktiv gæste-session sker der intet.
   */
  endGuestSession() {
    if (!this.guestTokens) return
    this.guestTokens = null
    this.user = null
    this.notifyListeners()
  }

  clearTokens() {
    this.guestTokens = null
    this.user = null
    try {
      localStorage.removeItem(this.storageKeyAccess)
      localStorage.removeItem(this.storageKeyRefresh)
      localStorage.removeItem(this.storageKeyUser)
    } catch {
      // Ignore storage errors
    }
    this.notifyListeners()
  }

  subscribe(callback: AuthChangeCallback): () => void {
    this.listeners.add(callback)
    // Call immediately with current state
    callback(this.user)
    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback)
    }
  }

  private notifyListeners() {
    this.listeners.forEach(callback => callback(this.user))
  }
}

// Singleton instance - init() must be called with API key before use
export const authStore = new AuthStore()
